import vulnerabilitiesApi, { fetchVulnerabilities, findVulnerability, fetchVulnerabilitiesProperties, getNote, upsertNote, deleteNote, fetchVulnerabilitiesPropertiesCount, fetchVulnerabilitiesPropertiesCountByKind } from './vulnerabilities.api.js';
import tagService from "../tag/service.js";


/* Declare vulnerabilities data here to avoid a VSO annoying highlight */

const vulnerabilitiesData = $state([]);
const vulnerabilitiesListData = $state([])

/* Vulnerabilities state structure */

export const vulnerabilities = $state({
    data: vulnerabilitiesData,
    list: {
        data: vulnerabilitiesListData,
        loading: false,
        errors: null,
        loaded: false
    },
    loading: false,
    errors: null
});

/*
    START STATE ABM FUNCTIONS
*/

const updateVulnerabilities = (updatedVulnerabilities = []) => {
    if (updatedVulnerabilities.length < 1) return;

    const index = new Map(
        vulnerabilitiesData.map(vulnerability => [vulnerability.id, vulnerability])
    );

    for (const updated of updatedVulnerabilities) {
        const existing = index.get(updated.id);
        if (existing) {
            Object.assign(existing, updated);
        }
    }
};


const updateVulnerabilitiesList = (updatedVulnerabilities = []) => {
    if (!updatedVulnerabilities.length) return;

    const index = new Map(
        vulnerabilities.list.data.map((a, i) => [a.id, i])
    );

    for (const updated of updatedVulnerabilities) {
        const idx = index.get(updated.id);

        if (idx !== undefined) {
            vulnerabilities.list.data[idx] = {
                ...vulnerabilities.list.data[idx],
                ...updated
            };
        }
        else {
            vulnerabilities.list.data.push(updated);
        }
    }
};

/*
    Orchestration logic
*/

function setIDasFallback(vuls) {
    if (Array.isArray(vuls)) {
        for (let vul of vuls) {
            if (!vul.title) {
                vul.title = vul.id;
            }
            if (!vul.title_es) {
                vul.title_es = vul.title || vul.id;
            }
        }
    }
    return vuls;
}
let searchVulnerabilitiesController;

export const cancelVulnerabilitySearch = () => {
    // ? operator checks if searchVulnerabilitiesController exists or doesn't
    searchVulnerabilitiesController?.abort();
};

export const searchVulnerabilities = async (
    resource = 'v2/vulnerability/',
    config = { filter: {} }
) => {

    // Abort previous request if exists
    searchVulnerabilitiesController?.abort();

    // Create local controller for this request
    const controller = new AbortController();
    searchVulnerabilitiesController = controller;

    // check if this request is still the latest
    const isCurrent = () => searchVulnerabilitiesController === controller;

    // controller.signal.addEventListener('abort', () => {
    //     console.log('VULNERABILITY SEARCH ABORTED', config);
    // });

    vulnerabilities.loading = true;
    vulnerabilities.errors = null;

    let response;

    try {
        // Pass the controller signal to the fetch
        response = await fetchVulnerabilities(resource, { ...config, signal: controller.signal });

        // Always run setIDasFallback
        response = await setIDasFallback(response);

        if (!isCurrent()) return; // discard if a newer search started

        replaceVulnerabilities(response);

    } catch (err) {
        if (err.name === 'AbortError') {
            if (isCurrent()) searchVulnerabilitiesController = null; // cleanup
            return; // silently cancel
        }

        if (!isCurrent()) return;

        replaceVulnerabilities([]);
        vulnerabilities.errors = err;
        return [];
    } finally {
        if (!isCurrent()) return;

        vulnerabilities.loading = false;
        replaceVulnerabilitiesListElements(vulnerabilities.data);

        // Hydrate in finally (same as searchAssets buildList)
        if (config.buildList) {
            vulnerabilities.list.loading = true;

            response = await hydrateVulnerabilitiesWithTags(response, config.buildList.tags.elements, config.buildList.tags.associations, controller.signal);
            if (!isCurrent()) return;

            replaceVulnerabilitiesListElements(response);
            vulnerabilities.list.loading = false;
            vulnerabilities.list.loaded = true;
            if (isCurrent()) searchVulnerabilitiesController = null;
            return vulnerabilities.list.data;
        }

        // Cleanup controller if still current
        if (isCurrent()) searchVulnerabilitiesController = null;

        return vulnerabilities.data;
    }
};

export async function getVulnerabilityDetail(id, config = {}) {
    let vul;
    return findVulnerability(id, config)
        .catch(() => {
            return {};
        })
        .then(pVul => {
            vul = pVul;
            return vul;
        })
    ;
}

export function searchProps(config = { filter: null }) {
    let propsPromise = vulnerabilitiesApi.fetchVulnerabilitiesProperties(config)
        .catch((errors) => {
            throw errors;
        })
        .then(pProps => {
            let props = pProps;
            return props;
        })
        ;
    return propsPromise;
}


export const searchVulnerabilitiesPropertiesCount = async(config = {filter: {}}) => {
    let propertiesPromise = fetchVulnerabilitiesPropertiesCount(config)
        .catch((e) => {
            return [];
        })
        .then(pProperties => {
            return pProperties;
        })
    return propertiesPromise;
}

export const searchVulnerabilitiesPropertiesCountByKind = async(config = {filter: {}}) => {
    let propertiesPromise = fetchVulnerabilitiesPropertiesCountByKind(config)
        .catch((e) => {
            return [];
        })
        .then(pProperties => {
            return pProperties;
        })
    return propertiesPromise;
}

export const hydrateVulnerabilitiesWithTags = async (data, elements, tagAssociations, signal) => {
    // abort early if signal is already aborted
    if (signal?.aborted) return data;

    let tags = [];
    let associations = [];

    const getTags = async (config = { filter: {} }) => {
        if (signal?.aborted) return [];
        return await tagService.search('v2/tag/', config);
    };

    const getTagAssociations = async (config = { filter: { target_object_type: "Vulnerability" } }) => {
        if (signal?.aborted) return [];
        return await tagService.search('v2/tag/association/', config);
    };

    if (!elements || elements.length < 1) {
        tags = await getTags();
        if (signal?.aborted) return data; // stop immediately if cancelled
    } else {
        tags = elements;
    }

    if (!tagAssociations || tagAssociations.length < 1) {
        associations = await getTagAssociations({ filter: { target_object_type: "Vulnerability" } });
        if (signal?.aborted) return data; // stop immediately if cancelled
    } else {
        associations = tagAssociations;
    }

    const tagById = new Map();
    for (const tag of tags) {
        tagById.set(tag.id, tag);
    }

    // Index associations by vuln id and store all tag ids
    const associationsByTarget = new Map();
    for (const association of associations) {
        if (!associationsByTarget.has(association.target_object_id)) {
            associationsByTarget.set(association.target_object_id, []);
        }
        associationsByTarget.get(association.target_object_id).push(association.tag_id);
    }


    // hydrate vulnerabilities with tags
    for (const vuln of data) {
        if (signal?.aborted) return data;

        const tagIds = associationsByTarget.get(vuln.id) || [];

        vuln.tag = tagIds
            .map(id => tagById.get(id))
            .filter(Boolean)
            .sort((a, b) => a.name.localeCompare(b.name))
        ;
    }

    return data;
};


/* Reset */


export const replaceVulnerabilities = (newVulnerabilities) => {
    if(newVulnerabilities){
        vulnerabilitiesData.length = 0;
        vulnerabilitiesData.push(...newVulnerabilities);
    }
};

export const replaceVulnerabilitiesListElements = (newVulnerabilities) => {
    if(newVulnerabilities){
        vulnerabilitiesListData.length = 0;
        vulnerabilitiesListData.push(...newVulnerabilities);
    }
};

export const resetVulnerabilities = () => {
    replaceVulnerabilities([]);
    replaceVulnerabilitiesListElements([]);

    vulnerabilities.loading = false;
    vulnerabilities.errors = null;
    vulnerabilities.list.loading = false;
    vulnerabilities.list.errors = null;
};
